import { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import {
    ArrowRight,
    RotateCcw,
    ChevronLeft,
    CheckCircle2,
    Sparkles,
    Scale,
    Landmark,
    Users,
    Building2,
    HeartHandshake,
    ShieldCheck,
    Briefcase,
    Gem,
    Mail,
    Loader2,
} from "lucide-react";
import CTAButtons from "@/components/CTAButtons";
import SectionHeader from "@/components/SectionHeader";
import { submitInquiry } from "@/lib/api";

/* ---------------------------------------------------------------------- */
/*  DECISION TREE                                                         */
/* ---------------------------------------------------------------------- */

const QUESTIONS = {
    q1: {
        id: "q1",
        step: 1,
        title: "What is the primary purpose of the entity?",
        helper: "We start with the single most important lens: profit motive vs. public benefit.",
        options: [
            { label: "To generate a profit.", next: "q2" },
            {
                label: "For public benefit, charity, or social activities.",
                result: "npc",
            },
        ],
    },
    q2: {
        id: "q2",
        step: 2,
        title:
            "Are you a group of licensed professionals (e.g., Doctors, Lawyers, Auditors) required to accept personal liability for professional negligence?",
        helper:
            "Certain regulated professions must incorporate as an Inc. so that directors remain jointly and severally liable.",
        options: [
            { label: "Yes.", result: "inc" },
            { label: "No.", next: "q3" },
        ],
    },
    q3: {
        id: "q3",
        step: 3,
        title:
            "Is the primary goal to hold and protect generational wealth/assets, rather than run active daily trading operations?",
        helper:
            "Trusts are asset-protection vehicles — not trading shells. The tax cost is steep but the ring-fencing is powerful.",
        options: [
            { label: "Yes.", result: "trust" },
            { label: "No, it is for active trading.", next: "q4" },
        ],
    },
    q4: {
        id: "q4",
        step: 4,
        title:
            "Do you plan to raise capital from the general public or list on a stock exchange?",
        helper:
            "Public companies (Ltd) face the heaviest audit & governance burden, but are the only vehicle permitted to list on the JSE.",
        options: [
            { label: "Yes.", result: "ltd" },
            { label: "No.", next: "q5" },
        ],
    },
    q5: {
        id: "q5",
        step: 5,
        title:
            "Do you plan to bring on private investors (Angel Investors or Venture Capitalists) who will require shares in exchange for funding?",
        helper:
            "Outside investment almost always requires a formal share register, MOI and cap-table — which is what a (Pty) Ltd provides.",
        options: [
            { label: "Yes.", result: "pty", note: "Investors require a formal share structure." },
            { label: "No, we are self-funded for now.", next: "q6" },
        ],
    },
    q6: {
        id: "q6",
        step: 6,
        title:
            "Do you anticipate high net profits where a flat 27% corporate tax rate would be more beneficial than paying personal income tax (up to 45%)?",
        helper:
            "Once sustainable profits exceed roughly R550k p.a., the 27% company rate generally beats personal sliding-scale tax.",
        options: [
            { label: "Yes, tax efficiency is a priority.", result: "pty" },
            { label: "No, profits will be lower initially.", next: "q7" },
        ],
    },
    q7: {
        id: "q7",
        step: 7,
        title:
            "Are you prepared to pay annual professional fees for formal financial statements, CIPC compliance, and corporate tax returns?",
        helper:
            "A (Pty) Ltd carries a real annual compliance spend. If that is not viable yet, we route you to a simpler structure.",
        options: [
            { label: "Yes, I have a budget for compliance.", next: "q8" },
            { label: "No, I need the lowest possible administrative burden and cost.", next: "q9" },
        ],
    },
    q8: {
        id: "q8",
        step: 8,
        title:
            "Do you need legal separation between your personal assets and the business's liabilities (Limited Liability)?",
        helper:
            "A company is a separate juristic person. If your personal home, car and savings must be protected — you need limited liability.",
        options: [
            { label: "Yes, protect my personal assets.", result: "pty" },
            { label: "No, personal liability is fine.", next: "q9" },
        ],
    },
    q9: {
        id: "q9",
        step: 9,
        title: "Will you be operating this business alone or with partners?",
        helper:
            "The final fork — solo founders land at a Sole Proprietor; co-founders default to a Partnership unless incorporated.",
        options: [
            { label: "Alone.", result: "sole" },
            { label: "With partners.", result: "partnership" },
        ],
    },
};

/* ---------------------------------------------------------------------- */
/*  RESULTS / ENTITY PROFILES                                             */
/* ---------------------------------------------------------------------- */

const RESULTS = {
    npc: {
        key: "npc",
        name: "Non Profit Company (NPC)",
        tagline: "Purpose-driven. Public benefit.",
        icon: HeartHandshake,
        blurb:
            "An NPC is the correct legal vehicle when the primary objective is public benefit, charity, religion, education, culture, or social activism — not shareholder profit. It is a juristic person governed by the Companies Act 2008 with at least three incorporators.",
        pros: [
            "Recognised juristic person — can hold property, contract, and sue in its own name.",
            "Eligible for Section 18A tax-exempt PBO status (donations become tax-deductible for donors).",
            "Builds donor, grantor and CSI funder credibility.",
            "Income that is used for its public benefit objects is generally exempt.",
        ],
        cons: [
            "Strictly no distribution of income or assets to members, directors or officers.",
            "Minimum three directors and an MOI that is heavily prescribed.",
            "Full CIPC annual return and, if trading, a tax return and possibly an audit.",
            "Cannot be used to build private/generational wealth.",
        ],
        bestFor: "NGOs, churches, sports clubs, foundations, community initiatives.",
    },
    inc: {
        key: "inc",
        name: "Personal Liability Company (Inc.)",
        tagline: "For regulated professions.",
        icon: Scale,
        blurb:
            "An Incorporated (Inc.) company is mandatory for many regulated professional practices in South Africa. It offers corporate personality, but directors remain jointly and severally liable for the debts and contractual liabilities of the company during their directorship.",
        pros: [
            "Complies with regulatory requirements for attorneys, auditors, medical practitioners, etc.",
            "Separate juristic person — can own assets, employ and contract.",
            "Flat 27% corporate tax rate.",
            "Clear professional governance framework under the Companies Act 2008.",
        ],
        cons: [
            "Directors are personally liable for contractual debts incurred during their tenure.",
            "Full CIPC annual returns, AFS, and independent review (or audit depending on PIS).",
            "Not appropriate for non-regulated trading or investment businesses.",
            "Exiting directors carry a tail of prior-period liability.",
        ],
        bestFor:
            "Law firms, audit firms, medical practices, and other regulated professionals required to accept personal liability.",
    },
    trust: {
        key: "trust",
        name: "Trust",
        tagline: "Generational wealth protection.",
        icon: Gem,
        blurb:
            "A Trust is not a company — it is a legal arrangement where a Founder transfers assets to Trustees to hold and administer for the benefit of Beneficiaries, governed by a Trust Deed under the Trust Property Control Act. Used primarily for asset protection and estate planning.",
        pros: [
            "Ring-fences assets away from personal and business creditors.",
            "Powerful estate-planning and succession tool — assets fall outside your deceased estate.",
            "Perpetual existence — survives the death of the founder.",
            "Flexible class of beneficiaries and conduit principle for income distribution.",
        ],
        cons: [
            "Flat 45% income tax rate and 36% effective CGT if income is retained.",
            "Trustees have strict fiduciary duties — enforced by the Master of the High Court.",
            "Not designed for active trading — SARS will attack sham arrangements.",
            "Setup and ongoing administration costs are non-trivial.",
        ],
        bestFor:
            "Family wealth, property holding structures, estate planning, special needs beneficiaries.",
    },
    ltd: {
        key: "ltd",
        name: "Public Company (Ltd)",
        tagline: "Ready for public capital.",
        icon: Landmark,
        blurb:
            "A Public Company (Ltd) is the only vehicle permitted to offer its shares to the general public and, if it meets JSE requirements, to list on a stock exchange. It carries the highest compliance burden under the Companies Act 2008.",
        pros: [
            "Unrestricted ability to raise capital from the public and institutional investors.",
            "JSE listing unlocks massive liquidity and valuation upside.",
            "Perpetual existence and fully separate legal personality.",
            "Highest credibility with banks, regulators and counterparties.",
        ],
        cons: [
            "Mandatory annual audit — no exceptions.",
            "Full King IV governance, audit committee, social & ethics committee, and JSE disclosures (if listed).",
            "Significant legal, sponsor, JSE, and auditor fees.",
            "Extensive public disclosure requirements.",
        ],
        bestFor:
            "High-growth businesses targeting a public listing or a widely-held shareholder base.",
    },
    pty: {
        key: "pty",
        name: "Private Company (Pty Ltd)",
        tagline: "The South African standard.",
        icon: Building2,
        blurb:
            "The (Pty) Ltd is the default vehicle for any serious trading business in South Africa. It is a separate juristic person, offers limited liability to shareholders, has a flat 27% corporate tax rate, and can accommodate shareholders, investors and a formal cap-table.",
        pros: [
            "Limited liability — personal assets are legally separated from business creditors.",
            "Flat 27% corporate tax rate — beats sliding scale once profits are meaningful.",
            "Perpetual existence — survives owner death, divorce, and exit.",
            "Investor-ready: MOI, share register, and a formal cap-table.",
        ],
        cons: [
            "Annual CIPC returns, AFS, independent review (or audit depending on PIS score).",
            "Real annual professional compliance cost (accounting, tax, CIPC).",
            "Directors carry statutory duties under s76 of the Companies Act.",
            "Dividends Withholding Tax of 20% when profits are distributed.",
        ],
        bestFor:
            "Virtually every profitable owner-managed trading, services, e-commerce or SaaS business in South Africa.",
    },
    sole: {
        key: "sole",
        name: "Sole Proprietor",
        tagline: "You are the business.",
        icon: Briefcase,
        blurb:
            "A Sole Proprietor is not a separate legal entity — it is simply an individual trading under their own name (or a registered business name). All income is taxed in your personal hands on the sliding scale of 18% – 45%.",
        pros: [
            "Cheapest and fastest to start — no CIPC registration required.",
            "Minimal annual compliance — no AFS or corporate tax return.",
            "You keep 100% of the after-tax profit directly.",
            "Perfect for testing a service or product before formalising.",
        ],
        cons: [
            "Unlimited personal liability — your home and savings are exposed.",
            "Sliding scale personal tax can reach 45% at higher income levels.",
            "Business ends on your death or incapacity — no perpetuity.",
            "Harder to raise funding or to sell the business cleanly.",
        ],
        bestFor:
            "Freelancers, consultants, side-hustles and low-risk solo services in their early stage.",
    },
    partnership: {
        key: "partnership",
        name: "Partnership",
        tagline: "Two or more founders, shared liability.",
        icon: Users,
        blurb:
            "A Partnership is a common-law contractual relationship between two or more persons carrying on business with a view to profit. It has no separate legal personality — each partner is taxed individually on their share of profits, and all partners are jointly and severally liable for the debts of the partnership.",
        pros: [
            "No CIPC registration — formed simply by a Partnership Agreement.",
            "Losses flow through to partners and can be offset against other income.",
            "Flexible profit-sharing arrangements via the Partnership Agreement.",
            "Low setup and ongoing compliance cost.",
        ],
        cons: [
            "Joint and several unlimited liability — one partner's debt is every partner's debt.",
            "Dissolves on death or exit of a partner unless the agreement provides otherwise.",
            "Sliding scale personal tax up to 45% on each partner's share.",
            "Disputes between partners can be costly — always have a written Partnership Agreement.",
        ],
        bestFor:
            "Two or more founders who want to trade together with low overhead and trust each other enough to accept joint liability.",
    },
};

/* ---------------------------------------------------------------------- */
/*  COMPARISON MATRIX DATA                                                */
/* ---------------------------------------------------------------------- */

const ENTITY_COLUMNS = [
    { key: "sole", short: "Sole Proprietor", sub: "Individual" },
    { key: "partnership", short: "Partnership", sub: "Common Law" },
    { key: "inc", short: "Personal Liab. Co", sub: "(Inc.)" },
    { key: "cc", short: "Close Corp", sub: "(CC)" },
    { key: "pty", short: "Private Co", sub: "(Pty) Ltd" },
    { key: "ltd", short: "Public Co", sub: "(Ltd)" },
    { key: "npc", short: "Non Profit", sub: "(NPC)" },
    { key: "trust", short: "Trust", sub: "Trust Deed" },
];

const MATRIX_GROUPS = [
    {
        title: "Legal & Governance",
        icon: Scale,
        rows: [
            {
                feature: "Separate Legal Entity?",
                values: ["No", "No", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes (for tax/admin)"],
            },
            {
                feature: "Owner Liability",
                values: [
                    "Unlimited",
                    "Unlimited (Joint & Several)",
                    "Limited (but Directors liable for negligence)",
                    "Limited",
                    "Limited",
                    "Limited",
                    "Limited (Guarantors)",
                    "Limited",
                ],
            },
            {
                feature: "Governing Legislation",
                values: [
                    "Common Law",
                    "Common Law",
                    "Companies Act 2008",
                    "CC Act 1984",
                    "Companies Act 2008",
                    "Companies Act 2008",
                    "Companies Act 2008",
                    "Trust Property Control Act",
                ],
            },
            {
                feature: "Founding Document",
                values: [
                    "None",
                    "Partnership Agreement",
                    "MOI (Memo of Incorporation)",
                    "Founding Statement",
                    "MOI",
                    "MOI",
                    "MOI",
                    "Trust Deed",
                ],
            },
            {
                feature: "Management By",
                values: [
                    "Owner",
                    "Partners",
                    "Directors",
                    "Members",
                    "Directors",
                    "Directors",
                    "Directors",
                    "Trustees",
                ],
            },
        ],
    },
    {
        title: "Ownership & Capital",
        icon: Users,
        rows: [
            {
                feature: "Owners Designated As",
                values: [
                    "Owner",
                    "Partners",
                    "Shareholders",
                    "Members",
                    "Shareholders",
                    "Shareholders",
                    "N/A (Guarantors)",
                    "Beneficiaries",
                ],
            },
            {
                feature: "Capital Raising",
                values: [
                    "Personal / Bank",
                    "Partners / Bank",
                    "Private Issue",
                    "Member Contributions",
                    "Private Issue / VC",
                    "Public Exchange (JSE)",
                    "Donations / Grants",
                    "Donor / Founder",
                ],
            },
            {
                feature: "Continuity (Lifespan)",
                values: [
                    "Ends on death/insolvency",
                    "Ends on death/exit of partner",
                    "Perpetual",
                    "Perpetual",
                    "Perpetual",
                    "Perpetual",
                    "Perpetual",
                    "Perpetual (until dissolved)",
                ],
            },
        ],
    },
    {
        title: "Taxation (SARS Rates · 2025/2026)",
        icon: Landmark,
        rows: [
            {
                feature: "Taxable Entity",
                values: [
                    "The Individual",
                    "The Individual Partners",
                    "The Company",
                    "The Corporation",
                    "The Company",
                    "The Company",
                    "The Company (Exemptions apply)",
                    "The Trust",
                ],
            },
            {
                feature: "Income Tax Rate",
                values: [
                    "18% – 45% (Sliding Scale)",
                    "18% – 45% (Sliding Scale)",
                    "27% Flat Rate",
                    "27% Flat Rate",
                    "27% Flat Rate",
                    "27% Flat Rate",
                    "27% (If trading)",
                    "45% Flat Rate",
                ],
            },
            {
                feature: "Effective Capital Gains Tax",
                values: [
                    "Max 18%",
                    "Max 18%",
                    "21.6%",
                    "21.6%",
                    "21.6%",
                    "21.6%",
                    "21.6%",
                    "36%",
                ],
            },
            {
                feature: "Dividends Withholding Tax",
                values: ["N/A", "N/A", "20%", "20%", "20%", "20%", "N/A", "N/A"],
            },
        ],
    },
    {
        title: "Compliance & Assurance",
        icon: ShieldCheck,
        rows: [
            {
                feature: "CIPC Annual Returns",
                values: [
                    "No",
                    "No",
                    "Yes",
                    "Yes",
                    "Yes",
                    "Yes",
                    "Yes",
                    "No (Master of High Court)",
                ],
            },
            {
                feature: "Mandatory Audit?",
                values: [
                    "No",
                    "No",
                    "PIS Score Dependent",
                    "PIS Score Dependent",
                    "PIS Score Dependent",
                    "Yes, always",
                    "PIS Score Dependent",
                    "Trust Deed Dependent",
                ],
            },
            {
                feature: "Accounting Officer / Review",
                values: [
                    "No",
                    "No",
                    "Independent Review (if no audit)",
                    "Accounting Officer",
                    "Independent Review (if no audit)",
                    "N/A (Audit req.)",
                    "Independent Review (if no audit)",
                    "No (Unless in Deed)",
                ],
            },
        ],
    },
];

/* ---------------------------------------------------------------------- */
/*  PAGE COMPONENT                                                        */
/* ---------------------------------------------------------------------- */

export default function Structures() {
    /* Wizard state */
    const [currentId, setCurrentId] = useState("q1");
    const [history, setHistory] = useState([]); // [{qId, optionIndex}]
    const [resultKey, setResultKey] = useState(null);
    const [animKey, setAnimKey] = useState(0);

    const current = resultKey ? null : QUESTIONS[currentId];
    const result = resultKey ? RESULTS[resultKey] : null;

    const progress = useMemo(() => {
        if (result) return 100;
        // Step count is QUESTIONS[currentId].step (1..9)
        const step = current?.step || 1;
        return Math.min(100, Math.round((step / 9) * 100));
    }, [current, result]);

    useEffect(() => {
        setAnimKey((k) => k + 1);
    }, [currentId, resultKey]);

    function handleOption(opt) {
        setHistory((h) => [...h, { qId: currentId, label: opt.label }]);
        if (opt.result) {
            setResultKey(opt.result);
        } else if (opt.next) {
            setCurrentId(opt.next);
        }
    }

    function handleBack() {
        if (resultKey) {
            // undo result, return to last question in history
            setResultKey(null);
            const prev = history[history.length - 1];
            if (prev) {
                setHistory((h) => h.slice(0, -1));
                setCurrentId(prev.qId);
            }
            return;
        }
        if (history.length === 0) return;
        const prev = history[history.length - 1];
        setHistory((h) => h.slice(0, -1));
        setCurrentId(prev.qId);
    }

    function handleRestart() {
        setHistory([]);
        setResultKey(null);
        setCurrentId("q1");
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    /* Matrix column hover */
    const [hoverCol, setHoverCol] = useState(null);

    return (
        <div data-testid="page-structures">
            {/* -------------------- HERO -------------------- */}
            <section className="bg-white border-b border-tca-line relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60" />
                <div className="container-tca relative py-20 md:py-28">
                    <div className="max-w-3xl animate-fade-up">
                        <p className="overline text-tca-orange mb-4">
                            Entity Structures · Companies Act 2008 · SARS 2025/2026
                        </p>
                        <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                            Pick the right
                            <span className="block text-tca-orange">legal structure.</span>
                        </h1>
                        <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            Answer a few plain-English questions and we will point you at the
                            correct South African entity — from a Sole Proprietor right up to a
                            Public Company or Trust. Then compare every vehicle side-by-side in
                            one dense, honest matrix.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href="#wizard"
                                data-testid="structures-hero-cta-wizard"
                                className="btn-primary"
                            >
                                Start the Wizard <ArrowRight className="w-4 h-4" />
                            </a>
                            <a
                                href="#matrix"
                                data-testid="structures-hero-cta-matrix"
                                className="btn-secondary"
                            >
                                Jump to Comparison Matrix
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* -------------------- WIZARD -------------------- */}
            <section
                id="wizard"
                className="bg-tca-fog border-b border-tca-line scroll-mt-28"
            >
                <div className="container-tca py-20 md:py-24">
                    <SectionHeader
                        overline="Step 1 · Decision Tree"
                        title="Entity Structuring Questionnaire."
                        description="One question at a time. Nine total — most founders only need four or five to land on the correct structure."
                    />

                    <div className="mt-12 max-w-3xl mx-auto">
                        {/* Progress + step indicator */}
                        <div
                            data-testid="wizard-progress"
                            className="flex items-center justify-between gap-4 mb-6"
                        >
                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-tca-mute">
                                {result ? (
                                    <>
                                        <Sparkles className="w-3.5 h-3.5 text-tca-orange" />
                                        <span>Result Ready</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-tca-orange">
                                            Step {current.step}
                                        </span>
                                        <span className="text-tca-line">/</span>
                                        <span>9</span>
                                    </>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                {history.length > 0 && !result && (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        data-testid="wizard-back-btn"
                                        className="btn-ghost text-xs"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        Back
                                    </button>
                                )}
                                {(history.length > 0 || result) && (
                                    <button
                                        type="button"
                                        onClick={handleRestart}
                                        data-testid="wizard-restart-btn"
                                        className="inline-flex items-center gap-2 border border-tca-ink px-3 py-2 text-xs font-semibold tracking-wide text-tca-ink hover:bg-tca-ink hover:text-white transition-colors duration-200"
                                    >
                                        <RotateCcw className="w-3.5 h-3.5" />
                                        Start Over
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="h-1 w-full bg-tca-line mb-10 overflow-hidden">
                            <div
                                data-testid="wizard-progress-bar"
                                className="h-full bg-tca-orange transition-all duration-500 ease-out"
                                style={{ width: `${progress}%` }}
                            />
                        </div>

                        {/* Question / Result card */}
                        <div
                            key={animKey}
                            data-testid={result ? `wizard-result-${result.key}` : `wizard-question-${current.id}`}
                            className="bg-white border border-tca-line p-7 sm:p-10 lg:p-12 shadow-[0_1px_0_rgba(0,0,0,0.04)] animate-fade-up"
                        >
                            {current && !result && (
                                <QuestionCard question={current} onSelect={handleOption} />
                            )}
                            {result && (
                                <ResultCard
                                    result={result}
                                    history={history}
                                    onRestart={handleRestart}
                                />
                            )}
                        </div>

                        {/* Breadcrumb of answers so far */}
                        {history.length > 0 && (
                            <div
                                data-testid="wizard-trail"
                                className="mt-8 flex flex-wrap gap-2 text-[11px]"
                            >
                                {history.map((h, i) => (
                                    <span
                                        key={i}
                                        className="inline-flex items-center gap-1.5 border border-tca-line bg-white px-2.5 py-1.5 text-tca-mute"
                                    >
                                        <span className="font-bold text-tca-orange">
                                            Q{QUESTIONS[h.qId].step}
                                        </span>
                                        <span className="truncate max-w-[220px]">{h.label}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* -------------------- MATRIX -------------------- */}
            <section
                id="matrix"
                className="bg-white border-b border-tca-line scroll-mt-28"
            >
                <div className="container-tca py-20 md:py-24">
                    <SectionHeader
                        overline="Step 2 · Side-by-Side Matrix"
                        title="South African Legal Entity Comparison."
                        description="Every commonly-used SA structure compared across governance, ownership, taxation and compliance. Hover any column to isolate it."
                    />

                    <div className="mt-12 border border-tca-line bg-white overflow-hidden">
                        {/* Mobile scroll hint */}
                        <div className="md:hidden bg-tca-orangeSoft text-tca-orange text-[11px] uppercase tracking-[0.22em] font-bold px-4 py-2 border-b border-tca-line">
                            ← Swipe to compare all 8 entities →
                        </div>

                        <div className="overflow-x-auto">
                            <table
                                data-testid="entity-matrix"
                                className="w-full text-sm border-collapse min-w-[1100px]"
                            >
                                <thead>
                                    <tr className="bg-tca-ink text-white">
                                        <th
                                            scope="col"
                                            className="sticky left-0 z-20 bg-tca-ink text-white text-left font-display font-bold text-xs uppercase tracking-[0.16em] px-5 py-5 border-r border-tca-ink/60 min-w-[200px] w-[200px]"
                                        >
                                            Feature
                                        </th>
                                        {ENTITY_COLUMNS.map((col, idx) => {
                                            const isHover = hoverCol === idx;
                                            return (
                                                <th
                                                    key={col.key}
                                                    scope="col"
                                                    data-testid={`matrix-col-${col.key}`}
                                                    onMouseEnter={() => setHoverCol(idx)}
                                                    onMouseLeave={() => setHoverCol(null)}
                                                    className={`align-bottom px-4 py-4 text-left transition-colors duration-150 border-r border-white/10 last:border-r-0 ${
                                                        isHover ? "bg-tca-orange" : "bg-tca-ink"
                                                    }`}
                                                >
                                                    <div className="font-display font-bold text-[13px] leading-tight text-white">
                                                        {col.short}
                                                    </div>
                                                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold mt-1">
                                                        {col.sub}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>

                                <tbody>
                                    {MATRIX_GROUPS.map((group, gIdx) => {
                                        const GIcon = group.icon;
                                        return (
                                            <MatrixGroup
                                                key={gIdx}
                                                group={group}
                                                GIcon={GIcon}
                                                hoverCol={hoverCol}
                                                setHoverCol={setHoverCol}
                                                totalCols={ENTITY_COLUMNS.length}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <p className="mt-6 text-xs text-tca-mute max-w-3xl">
                        <span className="font-semibold text-tca-ink">Disclaimer:</span>{" "}
                        This matrix is a high-level summary for orientation only. Tax rates and
                        thresholds reflect SARS guidance for the 2025/2026 year of assessment and
                        may change. Always confirm your specific facts with a registered
                        professional before incorporating, restructuring or filing.
                    </p>
                </div>
            </section>

            {/* -------------------- CTA -------------------- */}
            <section className="bg-tca-fog">
                <div className="container-tca py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="overline text-tca-orange mb-3">Ready to register?</p>
                        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-tca-ink leading-[1.05]">
                            Let our CA(SA) team incorporate it properly the first time.
                        </h2>
                        <p className="mt-4 text-tca-mute">
                            CIPC registration, SARS tax number, beneficial ownership filing, MOI
                            drafting and a first-year compliance plan — all bundled in one
                            fixed-fee engagement.
                        </p>
                    </div>
                    <CTAButtons testIdPrefix="structures-cta" />
                </div>
            </section>
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/*  SUB-COMPONENTS                                                        */
/* ---------------------------------------------------------------------- */

function QuestionCard({ question, onSelect }) {
    return (
        <div>
            <p className="overline text-tca-orange mb-3">
                Question {question.step} of 9
            </p>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-tca-ink leading-tight">
                {question.title}
            </h3>
            {question.helper && (
                <p className="mt-3 text-sm text-tca-mute leading-relaxed">
                    {question.helper}
                </p>
            )}

            <div className="mt-8 grid gap-3">
                {question.options.map((opt, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onSelect(opt)}
                        data-testid={`wizard-${question.id}-option-${i}`}
                        className="group text-left border border-tca-line bg-white hover:bg-tca-ink hover:border-tca-ink px-5 py-4 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tca-orange"
                    >
                        <div className="flex items-start gap-4">
                            <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-8 h-8 border border-tca-line group-hover:border-tca-orange bg-tca-fog group-hover:bg-tca-orange text-tca-ink group-hover:text-white font-display font-bold text-sm transition-colors duration-150">
                                {String.fromCharCode(65 + i)}
                            </span>
                            <div className="flex-1">
                                <p className="text-[15px] font-semibold text-tca-ink group-hover:text-white transition-colors duration-150">
                                    {opt.label}
                                </p>
                                {opt.note && (
                                    <p className="mt-1 text-xs text-tca-mute group-hover:text-white/70 transition-colors duration-150">
                                        {opt.note}
                                    </p>
                                )}
                            </div>
                            <ArrowRight className="w-4 h-4 mt-1.5 text-tca-mute group-hover:text-tca-orange transition-colors duration-150" />
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

function ResultCard({ result, history, onRestart }) {
    const Icon = result.icon || CheckCircle2;
    return (
        <div>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="overline text-tca-orange mb-3 flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5" />
                        Your Recommended Structure
                    </p>
                    <h3 className="font-display font-extrabold text-3xl sm:text-4xl text-tca-ink leading-[1.05]">
                        {result.name}
                    </h3>
                    <p className="mt-2 text-tca-orange font-semibold text-sm tracking-wide">
                        {result.tagline}
                    </p>
                </div>
                <div className="w-16 h-16 bg-tca-orangeSoft text-tca-orange flex items-center justify-center shrink-0">
                    <Icon className="w-8 h-8" />
                </div>
            </div>

            <p className="mt-6 text-[15px] text-tca-mute leading-relaxed">
                {result.blurb}
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-tca-line border border-tca-line">
                <div className="bg-white p-5">
                    <p className="overline text-tca-orange mb-3">Strengths</p>
                    <ul className="space-y-2">
                        {result.pros.map((p) => (
                            <li key={p} className="flex items-start gap-2 text-sm text-tca-ink">
                                <CheckCircle2 className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="bg-white p-5">
                    <p className="overline text-tca-mute mb-3">Trade-offs</p>
                    <ul className="space-y-2">
                        {result.cons.map((c) => (
                            <li key={c} className="flex items-start gap-2 text-sm text-tca-ink">
                                <span className="inline-block w-4 h-4 mt-0.5 border border-tca-mute shrink-0" />
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="mt-6 bg-tca-orangeSoft border border-tca-orange/30 px-5 py-4">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-tca-orange mb-1">
                    Best suited for
                </p>
                <p className="text-sm text-tca-ink leading-relaxed">{result.bestFor}</p>
            </div>

            <div className="mt-10" data-testid="structures-lead-section">
                <StructuresLeadForm result={result} history={history} />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={onRestart}
                    data-testid="wizard-result-restart-btn"
                    className="btn-secondary"
                >
                    <RotateCcw className="w-4 h-4" />
                    Start Over
                </button>
                <a
                    href="#matrix"
                    data-testid={`wizard-cta-matrix-${result.key}`}
                    className="btn-ghost"
                >
                    Compare against all entities <ArrowRight className="w-4 h-4" />
                </a>
            </div>
        </div>
    );
}

function MatrixGroup({ group, GIcon, hoverCol, setHoverCol, totalCols }) {
    return (
        <>
            {/* Group header row */}
            <tr className="bg-tca-orangeSoft">
                <td
                    colSpan={totalCols + 1}
                    className="sticky left-0 z-10 bg-tca-orangeSoft px-5 py-3 border-y border-tca-orange/30"
                >
                    <div className="flex items-center gap-2.5 text-tca-orange font-display font-bold text-xs uppercase tracking-[0.22em]">
                        <GIcon className="w-3.5 h-3.5" />
                        {group.title}
                    </div>
                </td>
            </tr>
            {group.rows.map((row, rIdx) => {
                const isEven = rIdx % 2 === 0;
                return (
                    <tr
                        key={row.feature}
                        className={`${isEven ? "bg-white" : "bg-tca-fog"} border-t border-tca-line`}
                    >
                        <th
                            scope="row"
                            className={`sticky left-0 z-10 ${
                                isEven ? "bg-white" : "bg-tca-fog"
                            } text-left font-semibold text-[13px] text-tca-ink px-5 py-4 border-r border-tca-line min-w-[200px] w-[200px]`}
                        >
                            {row.feature}
                        </th>
                        {row.values.map((val, cIdx) => {
                            const isColHover = hoverCol === cIdx;
                            return (
                                <td
                                    key={cIdx}
                                    data-testid={`matrix-cell-${cIdx}-${rIdx}`}
                                    onMouseEnter={() => setHoverCol(cIdx)}
                                    onMouseLeave={() => setHoverCol(null)}
                                    className={`px-4 py-4 text-[13px] leading-snug align-top border-r border-tca-line last:border-r-0 transition-colors duration-150 ${
                                        isColHover
                                            ? "bg-tca-orange text-white font-medium"
                                            : "text-tca-ink"
                                    }`}
                                >
                                    {val}
                                </td>
                            );
                        })}
                    </tr>
                );
            })}
        </>
    );
}


/* ---------------------------------------------------------------------- */
/*  LEAD CAPTURE FORM (mirrors PackageMatchmaker pattern)                 */
/* ---------------------------------------------------------------------- */

const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

function buildStructuresAnswers(history) {
    return history.map((h) => ({
        question: QUESTIONS[h.qId]?.title || `Question ${QUESTIONS[h.qId]?.step || ""}`,
        answer: h.label,
    }));
}

function StructuresLeadForm({ result, history }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
    const [status, setStatus] = useState("idle"); // idle | loading | success
    const [errors, setErrors] = useState({});

    const emailTrim = form.email.trim();
    const nameTrim = form.name.trim();
    const emailValid = EMAIL_RE.test(emailTrim);
    const nameValid = nameTrim.length >= 2;
    const showEmailError = emailTrim.length > 0 && !emailValid;
    const canSubmit = nameValid && emailValid && status !== "loading";

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (!nameValid) er.name = "Please enter your full name.";
        if (!emailValid) er.email = "Please enter a valid email address.";
        return er;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("loading");
        try {
            await submitInquiry({
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                company: form.company.trim() || null,
                subject: `Entity Wizard — ${result.name}`,
                message:
                    `The Entity Structuring Wizard recommended a ${result.name} for ${form.name.trim()}. ` +
                    `Answers: ${history
                        .map((h) => `Q${QUESTIONS[h.qId].step} → ${h.label}`)
                        .join(" | ")}.`,
                source: "structures",
                structures: {
                    result_key: result.key,
                    result_name: result.name,
                    tagline: result.tagline,
                    blurb: result.blurb,
                    pros: result.pros,
                    cons: result.cons,
                    best_for: result.bestFor,
                    answers: buildStructuresAnswers(history),
                },
            });
            setStatus("success");
            toast.success("Recommendation sent — check your inbox in a few seconds.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Something went wrong. Please try again or contact us directly.");
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid="structures-lead-success"
                className="bg-white border border-tca-orange p-7 md:p-9 text-center"
            >
                <div className="w-12 h-12 mx-auto bg-tca-orange/10 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-tca-orange" />
                </div>
                <h3 className="font-display font-extrabold text-tca-ink text-2xl mb-2">
                    Thanks {form.name.split(" ")[0]} — your recommendation is on its way.
                </h3>
                <p className="text-tca-mute leading-relaxed max-w-lg mx-auto">
                    We've emailed the <strong className="text-tca-ink">{result.name}</strong>{" "}
                    summary (with a branded PDF) to {form.email}. A TC Auditors advisor will
                    follow up within one business day.
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid="structures-lead-form"
            className="bg-white border border-tca-line p-7 md:p-9"
            noValidate
        >
            <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-tca-orange/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-tca-orange" />
                </div>
                <div>
                    <h3 className="font-display font-extrabold text-tca-ink text-2xl leading-tight">
                        Email me this recommendation
                    </h3>
                    <p className="text-sm text-tca-mute mt-1">
                        We'll send you the {result.name} summary plus a branded PDF, and a TC
                        Auditors advisor will follow up to help you register. No spam, no
                        commitment.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Full name <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="structures-lead-name"
                        className={`w-full px-4 py-3 border bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors ${
                            errors.name ? "border-red-500" : "border-tca-line"
                        }`}
                        placeholder="Jane Smith"
                    />
                    {errors.name && (
                        <p className="text-xs text-red-600 mt-1">{errors.name}</p>
                    )}
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Email <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="structures-lead-email"
                        aria-invalid={showEmailError || Boolean(errors.email)}
                        className={`w-full px-4 py-3 border bg-white text-tca-ink focus:outline-none transition-colors ${
                            showEmailError || errors.email
                                ? "border-red-500 bg-red-50 focus:border-red-500"
                                : "border-tca-line focus:border-tca-orange"
                        }`}
                        placeholder="jane@company.co.za"
                    />
                    {(showEmailError || errors.email) && (
                        <p
                            className="text-xs text-red-600 mt-1"
                            data-testid="structures-lead-email-error"
                        >
                            {errors.email || "Please enter a valid email address."}
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Phone{" "}
                        <span className="text-tca-mute/60 normal-case tracking-normal font-normal">
                            (optional)
                        </span>
                    </label>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="structures-lead-phone"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="079 461 7545"
                    />
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Company{" "}
                        <span className="text-tca-mute/60 normal-case tracking-normal font-normal">
                            (optional)
                        </span>
                    </label>
                    <input
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        data-testid="structures-lead-company"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="Acme (Pty) Ltd"
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={!canSubmit}
                aria-disabled={!canSubmit}
                title={
                    !canSubmit && emailTrim.length > 0 && !emailValid
                        ? "Please enter a valid email address"
                        : undefined
                }
                data-testid="structures-lead-submit"
                className="btn-primary group mt-6 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {status === "loading" ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                ) : (
                    <>
                        Email Me My {result.name} Recommendation
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                )}
            </button>

            <p className="text-[11px] text-tca-mute mt-4">
                By submitting, you consent to TC Auditors contacting you about your inquiry. We
                will never share your details.
            </p>
        </form>
    );
}
